/**
 * HbarReleaseHistory — V2.J.3
 *
 * Chronological history of HBAR treasury releases detected by the
 * /api/cron/check-hbar-release cron. Renders :
 *
 *  - A compact SVG "supply growth" sparkline (released supply over
 *    time) — visual at-a-glance confirmation that releases are being
 *    tracked.
 *  - A scrollable list of events, latest first, with delta +
 *    cumulative released supply + relative timestamp.
 *
 * Falls back to a friendly empty state when no event has been
 * detected yet — common in the first weeks after surveillance is
 * enabled.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHbarReleaseEvents } from '../../hooks/useHbarReleaseEvents';

interface HbarReleaseHistoryProps {
  /** Max number of events to fetch / display. Default 30. */
  limit?: number;
}

export function HbarReleaseHistory({ limit = 30 }: HbarReleaseHistoryProps) {
  const { t, i18n } = useTranslation();
  const { events, isLoading } = useHbarReleaseEvents({ limit });

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });

  // Sort ascending for chart, keep descending (latest first) for list.
  const chronoEvents = useMemo(() => [...events].reverse(), [events]);

  return (
    <section
      aria-labelledby="hbar-release-history-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '14px',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2
            id="hbar-release-history-title"
            style={{
              fontSize: '15px',
              fontWeight: 700,
              margin: 0,
              color: 'var(--text)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span aria-hidden style={{ color: '#00C2FF' }}>ℏ</span>
            {t('hedera.history.title', 'Historique des libérations')}
          </h2>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
            {t(
              'hedera.history.subtitle',
              'Releases détectés depuis l\'activation de la surveillance horaire',
            )}
          </div>
        </div>
        {events.length > 0 && (
          <span
            style={{
              fontSize: '11px',
              color: 'var(--muted)',
              padding: '4px 8px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            {events.length} {t('hedera.history.eventCount', 'événements')}
          </span>
        )}
      </header>

      {isLoading ? (
        <SkeletonHistory />
      ) : events.length === 0 ? (
        <EmptyHistory t={t} />
      ) : (
        <>
          <SupplyChart events={chronoEvents} locale={locale} fmt={fmt} t={t} />
          <ul
            style={{
              listStyle: 'none',
              margin: '14px 0 0',
              padding: 0,
              maxHeight: '320px',
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: '10px',
            }}
          >
            {events.map((ev, idx) => (
              <li
                key={ev.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 14px',
                  borderBottom:
                    idx === events.length - 1 ? 'none' : '1px solid var(--border)',
                  fontSize: '13px',
                  background:
                    idx === 0 ? 'rgba(0, 194, 255, 0.04)' : 'transparent',
                }}
              >
                <div
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: idx === 0 ? '#00C2FF' : 'var(--muted)',
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      color: 'var(--text)',
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    +{fmt(ev.delta_hbar)} HBAR
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: '11px', marginTop: '2px' }}>
                    {formatAbsoluteDate(ev.detected_at, locale)} ·{' '}
                    {t('hedera.history.totalAfter', 'Total libéré')} :{' '}
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(ev.new_released_hbar)}
                    </span>
                  </div>
                </div>
                {ev.note && ev.note !== 'dry-run' && (
                  <span
                    style={{
                      fontSize: '10px',
                      color: 'var(--muted)',
                      padding: '2px 6px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                    }}
                  >
                    {ev.note}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Mini supply chart
// ─────────────────────────────────────────────────────────────────────

interface SupplyChartProps {
  events: Array<{ detected_at: string; new_released_hbar: number; delta_hbar: number }>;
  locale: string;
  fmt: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function SupplyChart({ events, locale, fmt, t }: SupplyChartProps) {
  const chartData = useMemo(() => {
    if (events.length === 0) return null;
    const values = events.map((e) => e.new_released_hbar);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const width = 600;
    const height = 80;
    const padding = 4;
    const innerW = width - padding * 2;
    const innerH = height - padding * 2;

    const points = events.map((ev, idx) => {
      const x =
        events.length === 1
          ? width / 2
          : padding + (idx / (events.length - 1)) * innerW;
      const y = padding + innerH - ((ev.new_released_hbar - min) / range) * innerH;
      return { x, y, ev };
    });

    const linePath = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');

    const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(2)} ${height - padding} L ${points[0].x.toFixed(2)} ${height - padding} Z`;

    return { points, linePath, areaPath, width, height, min, max };
  }, [events]);

  if (!chartData) return null;

  const firstEv = events[0];
  const lastEv = events[events.length - 1];

  return (
    <div
      style={{
        position: 'relative',
        background: 'rgba(0, 194, 255, 0.04)',
        border: '1px solid rgba(0, 194, 255, 0.15)',
        borderRadius: '10px',
        padding: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          color: 'var(--muted)',
          marginBottom: '6px',
        }}
      >
        <span>
          {t('hedera.history.first', 'Premier')} ·{' '}
          {formatAbsoluteDate(firstEv.detected_at, locale)}
        </span>
        <span>
          {t('hedera.history.last', 'Dernier')} ·{' '}
          {formatAbsoluteDate(lastEv.detected_at, locale)}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${chartData.width} ${chartData.height}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '80px', display: 'block' }}
        aria-label={t(
          'hedera.history.chartAlt',
          'Évolution de la supply HBAR libérée au cours du temps',
        )}
        role="img"
      >
        <defs>
          <linearGradient id="hbarChartGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00C2FF" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#00C2FF" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={chartData.areaPath} fill="url(#hbarChartGradient)" />
        <path
          d={chartData.linePath}
          fill="none"
          stroke="#00C2FF"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {chartData.points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={i === chartData.points.length - 1 ? 3.5 : 2}
            fill={i === chartData.points.length - 1 ? '#00C2FF' : 'rgba(0, 194, 255, 0.7)'}
            stroke="rgba(0, 0, 0, 0.4)"
            strokeWidth="1"
          >
            <title>
              {formatAbsoluteDate(p.ev.detected_at, locale)} — +{fmt(p.ev.delta_hbar)} HBAR
            </title>
          </circle>
        ))}
      </svg>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '10px',
          color: 'var(--muted)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>{fmt(chartData.min)} HBAR</span>
        <span>{fmt(chartData.max)} HBAR</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Empty / loading states
// ─────────────────────────────────────────────────────────────────────

function EmptyHistory({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        padding: '24px',
        textAlign: 'center',
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px dashed var(--border)',
        borderRadius: '10px',
      }}
    >
      <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.5 }} aria-hidden>
        ℏ
      </div>
      <div style={{ fontSize: '13px', color: 'var(--text)', fontWeight: 600 }}>
        {t('hedera.history.emptyTitle', 'Aucune libération détectée pour le moment')}
      </div>
      <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
        {t(
          'hedera.history.emptyBody',
          'La surveillance horaire est active. Les prochains releases seront listés ici.',
        )}
      </div>
    </div>
  );
}

function SkeletonHistory() {
  return (
    <div
      style={{
        padding: '24px',
        background: 'rgba(255, 255, 255, 0.02)',
        borderRadius: '10px',
      }}
      aria-hidden
    >
      <div
        style={{
          height: '60px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '6px',
          marginBottom: '12px',
        }}
      />
      <div
        style={{
          height: '40px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '6px',
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Date helper
// ─────────────────────────────────────────────────────────────────────

function formatAbsoluteDate(iso: string, locale: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(locale === 'fr' ? 'fr-FR' : locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
